/**
 * Named Color Database — consolidated from SPA and shared-logic implementations.
 *
 * Provides:
 * - Indexed lookups by id, name, hex
 * - Fuzzy search with Levenshtein distance
 * - Token-based search with aliases
 * - Source filtering (css, xkcd)
 * - Pagination
 * - Delta-E nearest color resolution
 *
 * Backwards-compatible: loadColorDatabases, findNearestColors,
 * searchColorsByName, listNamedColors all keep their existing signatures.
 */
import cssColorNames from './css-color-names.json' with { type: 'json' };
import xkcdColorData from './xkcd-colors.json' with { type: 'json' };
import { calculateDeltaE } from './colorEngine.js';
// ── Internal state ──────────────────────────────────────────────────────────
let loaded = false;
let allEntries = [];
const byId = new Map();
const byName = new Map();
const byHex = new Map();
// ── Helpers ─────────────────────────────────────────────────────────────────
function normalise(value) {
    return value.trim().toLowerCase();
}
function toDisplayName(raw) {
    const cleaned = raw.replace(/[_-]+/g, ' ');
    return cleaned
        .split(/\s+/)
        .filter(Boolean)
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join(' ')
        .replace(/\b(Oklch|Rgb|Cmyk|Lab|Lch|Xyz|Hex)\b/gi, (m) => m.toUpperCase());
}
function deriveAliases(name) {
    const aliases = [];
    if (name.includes('grey')) {
        aliases.push(name.replace(/grey/g, 'gray'));
    }
    else if (name.includes('gray')) {
        aliases.push(name.replace(/gray/g, 'grey'));
    }
    return aliases.map(normalise);
}
function buildTokens(name, aliases) {
    const bucket = new Set();
    const register = (value) => {
        const n = normalise(value);
        if (!n)
            return;
        bucket.add(n);
        n.split(/\s+/).forEach((token) => { if (token)
            bucket.add(token); });
    };
    register(name);
    aliases.forEach(register);
    return Array.from(bucket);
}
function ensureHex(value) {
    const prefixed = value.startsWith('#') ? value : `#${value}`;
    return prefixed.toUpperCase();
}
function registerEntry(entry) {
    byId.set(entry.id, entry);
    const identifiers = new Set([entry.name, ...entry.aliases]);
    identifiers.forEach((id) => byName.set(id, entry));
    const variants = byHex.get(entry.hex) ?? [];
    variants.push(entry);
    byHex.set(entry.hex, variants);
}
function filterBySources(entries, sources) {
    if (!sources || sources.length === 0)
        return entries;
    const allowed = new Set(sources);
    return entries.filter((e) => allowed.has(e.source));
}
function levenshtein(a, b) {
    const matrix = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
    for (let i = 0; i <= a.length; i++)
        matrix[i][0] = i;
    for (let j = 0; j <= b.length; j++)
        matrix[0][j] = j;
    for (let i = 1; i <= a.length; i++) {
        for (let j = 1; j <= b.length; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            matrix[i][j] = Math.min(matrix[i - 1][j] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j - 1] + cost);
        }
    }
    return matrix[a.length][b.length];
}
// ── Database loading ────────────────────────────────────────────────────────
export async function loadColorDatabases() {
    if (loaded)
        return;
    try {
        const cssMap = cssColorNames;
        const xkcdRaw = xkcdColorData;
        // Build CSS entries
        const cssEntries = Object.entries(cssMap).map(([rawName, hex]) => {
            const name = normalise(rawName);
            const aliases = deriveAliases(name);
            const displayName = toDisplayName(rawName);
            return {
                id: `css:${name}`,
                name,
                displayName,
                hex: ensureHex(hex),
                source: 'css',
                aliases,
                tokens: buildTokens(displayName, aliases),
            };
        });
        // Build XKCD entries
        const xkcdEntries = (xkcdRaw.colors ?? []).map(({ color, hex }) => {
            const name = normalise(color);
            const displayName = toDisplayName(color);
            return {
                id: `xkcd:${name}`,
                name,
                displayName,
                hex: ensureHex(hex),
                source: 'xkcd',
                aliases: [],
                tokens: buildTokens(displayName, []),
            };
        });
        allEntries = [...cssEntries, ...xkcdEntries];
        byId.clear();
        byName.clear();
        byHex.clear();
        allEntries.forEach(registerEntry);
        loaded = true;
    }
    catch {
        allEntries = [];
        loaded = false;
    }
}
// ── Query functions ─────────────────────────────────────────────────────────
/**
 * Get all named color entries, optionally filtered by source.
 */
export async function getNamedColorEntries(options) {
    await loadColorDatabases();
    return filterBySources(allEntries, options?.sources);
}
/**
 * Find an exact named color by its name (case-insensitive, alias-aware).
 */
export async function findNamedColorByName(name, options) {
    await loadColorDatabases();
    const entry = byName.get(normalise(name));
    if (!entry)
        return null;
    if (options?.sources && options.sources.length > 0 && !options.sources.includes(entry.source)) {
        return null;
    }
    return entry;
}
/**
 * Find named colors by hex value.
 */
export async function findNamedColorsByHex(hex, options) {
    await loadColorDatabases();
    const entries = byHex.get(ensureHex(hex)) ?? [];
    return filterBySources(entries, options?.sources);
}
/**
 * Get a named color by its canonical ID.
 */
export async function getNamedColorById(id) {
    await loadColorDatabases();
    return byId.get(id) ?? null;
}
/**
 * Fuzzy search named colors with token matching, prefix matching,
 * substring matching, and Levenshtein distance fallback.
 */
export async function searchNamedColorEntries(query, options) {
    await loadColorDatabases();
    const normalizedQuery = normalise(query);
    if (!normalizedQuery)
        return [];
    const candidates = filterBySources(allEntries, options?.sources);
    const results = [];
    for (const entry of candidates) {
        let score = null;
        let exact = false;
        if (entry.name === normalizedQuery || entry.aliases.includes(normalizedQuery)) {
            score = 0;
            exact = true;
        }
        else if (entry.tokens.includes(normalizedQuery)) {
            score = 0.1;
        }
        else if (entry.name.startsWith(normalizedQuery)) {
            score = 0.2;
        }
        else if (entry.tokens.some((t) => t.startsWith(normalizedQuery))) {
            score = 0.3;
        }
        else {
            const displayLower = entry.displayName.toLowerCase();
            if (displayLower.includes(normalizedQuery)) {
                score = 0.5;
            }
            else {
                const distance = levenshtein(normalizedQuery, entry.name);
                const normalizedDistance = distance / Math.max(entry.name.length, normalizedQuery.length, 1);
                if (normalizedDistance <= 0.5) {
                    score = 1 + normalizedDistance;
                }
            }
        }
        if (score === null)
            continue;
        results.push({ entry, score, exact });
    }
    results.sort((a, b) => {
        if (a.score !== b.score)
            return a.score - b.score;
        if (a.exact !== b.exact)
            return a.exact ? -1 : 1;
        if (a.entry.source !== b.entry.source)
            return a.entry.source.localeCompare(b.entry.source);
        return a.entry.displayName.localeCompare(b.entry.displayName);
    });
    const limit = options?.limit ?? 20;
    return results.slice(0, limit);
}
/**
 * Get named color stats (counts per source).
 */
export async function getNamedColorStats() {
    await loadColorDatabases();
    const css = allEntries.filter((e) => e.source === 'css').length;
    const xkcd = allEntries.filter((e) => e.source === 'xkcd').length;
    return { css, xkcd, total: allEntries.length };
}
// ── Backward-compatible functions ───────────────────────────────────────────
/**
 * Find nearest named colors by Delta-E distance.
 * (Backward-compatible with original shared-logic API.)
 */
export async function findNearestColors(hex, method = '2000', limit = 10) {
    await loadColorDatabases();
    const results = await Promise.all(allEntries.map(async (entry) => ({
        name: entry.name,
        hex: entry.hex,
        deltaE: await calculateDeltaE(hex, entry.hex, method),
        source: entry.source,
    })));
    return results.sort((a, b) => a.deltaE - b.deltaE).slice(0, limit);
}
/**
 * Simple substring search by name.
 * (Backward-compatible with original shared-logic API.)
 */
export async function searchColorsByName(query, limit = 10) {
    await loadColorDatabases();
    const lowerQuery = query.toLowerCase();
    return allEntries
        .filter((e) => e.name.includes(lowerQuery))
        .slice(0, limit)
        .map((e) => ({ name: e.name, hex: e.hex, deltaE: 0, source: e.source }));
}
/**
 * List named colors with pagination and source filtering.
 * (Backward-compatible with original shared-logic API.)
 */
export async function listNamedColors(source = 'all', page = 1, limit = 20) {
    await loadColorDatabases();
    const sources = source === 'all' ? undefined : [source];
    const filtered = filterBySources(allEntries, sources);
    const start = (page - 1) * limit;
    const paginated = filtered.slice(start, start + limit);
    return {
        colors: paginated.map((e) => ({ name: e.name, hex: e.hex, source: e.source })),
        total: filtered.length,
    };
}
//# sourceMappingURL=namedColorDB.js.map